import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { tasksService, type Task, type TaskInput } from '../../../services/projectManagement/tasksService';
import SubtasksPanel from './SubtasksPanel';
import CommentsPanel from './CommentsPanel';
import ActivityFeedPanel from './ActivityFeedPanel';
import AttachmentsPanel from './AttachmentsPanel';
import RecurrenceSelector, { type RecurrenceRule } from './RecurrenceSelector';
import CustomFieldInput from './CustomFieldInput';
import { pmCustomFieldsApiService } from '../../../services/projectManagement/pmCustomFieldsApiService';
import type { CustomField } from '../../../services/projectManagement/customFieldsService';
import { pmSectionsApiService, type ProjectSection } from '../../../services/projectManagement/pmSectionsApiService';
import { pmMembersService, buildMemberMap, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { pmWatchersApiService } from '../../../services/projectManagement/pmWatchersApiService';

interface TaskDetailModalProps {
  taskId: string;
  organizationId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  taskId,
  organizationId,
  onClose,
  onUpdate,
}) => {
  const { user } = useAuth();
  const [task, setTask] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState('');
  
  // Editable fields
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editEstimatedTime, setEditEstimatedTime] = useState(0);
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);
  const [editAssigneeId, setEditAssigneeId] = useState<string>('');
  const [editSectionId, setEditSectionId] = useState<string>('');
  const [isWatching, setIsWatching] = useState(false);
  const [watchLoading, setWatchLoading] = useState(false);

  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  const fetchTaskDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await tasksService.getById(taskId, organizationId);
      if (error) {
        toast.error('Failed to load task details');
        return;
      }
      setTask(data);
      setCustomFieldValues(data.custom_fields || {});
      setEditTitle(data.title);
      setEditDescription(data.description || '');
      setEditStatus(data.status);
      setEditPriority(data.priority);
      setEditDueDate(data.due_date || '');
      setEditEstimatedTime(data.estimated_time || 0);
      setEditAssigneeId(data.assignee_id || '');
      setEditSectionId(data.section_id || '');
      setRecurrenceRule(data.recurrence_rule || null);
      setRecurrenceEndDate(data.recurrence_end_date || null);

      const { data: watchData, error: watchError } = await pmWatchersApiService.me({
        organization_id: organizationId,
        entity_type: 'task',
        entity_id: taskId,
      });
      if (!watchError) {
        setIsWatching(!!watchData?.watching);
      } else {
        setIsWatching(false);
      }

      const { data: memberData, error: memberError } = await pmMembersService.list(organizationId);
      if (!memberError) {
        setMembers(memberData);
      } else {
        setMembers([]);
      }

      const projectId = String(data.project_id || '');
      if (projectId) {
        const [{ data: fields, error: fieldsError }, { data: sectionRows, error: sectionsError }] = await Promise.all([
          pmCustomFieldsApiService.list({
            organization_id: organizationId,
            project_id: projectId,
          }),
          pmSectionsApiService.list({
            organization_id: organizationId,
            project_id: projectId,
          }),
        ]);

        if (fieldsError) {
          setCustomFields([]);
        } else {
          setCustomFields(fields || []);
        }

        if (sectionsError) {
          setSections([]);
        } else {
          setSections(sectionRows || []);
        }
      } else {
        setCustomFields([]);
        setSections([]);
      }
    } catch (error) {
      toast.error('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWatch = async () => {
    if (!organizationId) return;
    setWatchLoading(true);
    try {
      if (isWatching) {
        const { error } = await pmWatchersApiService.unwatch({
          organization_id: organizationId,
          entity_type: 'task',
          entity_id: taskId,
        });
        if (error) {
          toast.error('Failed to unwatch');
          return;
        }
        setIsWatching(false);
        toast.success('Unwatched');
        return;
      }

      const { error } = await pmWatchersApiService.watch({
        organization_id: organizationId,
        entity_type: 'task',
        entity_id: taskId,
      });
      if (error) {
        toast.error('Failed to watch');
        return;
      }
      setIsWatching(true);
      toast.success('Watching');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update watch status');
    } finally {
      setWatchLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const updates: Partial<TaskInput> = {
        title: editTitle,
        description: editDescription,
        status: editStatus as any,
        priority: editPriority as any,
        due_date: editDueDate || undefined,
        estimated_time: editEstimatedTime,
        assignee_id: editAssigneeId || null,
        section_id: editSectionId || null,
        custom_fields: customFieldValues,
        is_recurring: !!recurrenceRule,
        recurrence_rule: recurrenceRule,
        recurrence_end_date: recurrenceEndDate,
      };

      const { error } = await tasksService.update(taskId, organizationId, updates);
      if (error) {
        toast.error('Failed to update task');
        return;
      }

      toast.success('Task updated successfully');
      setEditing(false);
      onUpdate();
      await fetchTaskDetails();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      const { error } = await tasksService.delete(taskId, organizationId);
      if (error) {
        toast.error('Failed to delete task');
        return;
      }

      toast.success('Task deleted successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'low': return '🟢';
      case 'medium': return '🟡';
      case 'high': return '🟠';
      case 'urgent': return '🔴';
      default: return '⚪';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'backlog': return 'bg-gray-100 text-gray-800';
      case 'todo': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-purple-100 text-purple-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'done': return 'bg-green-100 text-green-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-4xl rounded-xl bg-white p-8 shadow-xl">
          <div className="text-center">Loading task details...</div>
        </div>
      </div>
    );
  }

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-4xl rounded-xl bg-white shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {editing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full text-2xl font-bold border-b-2 border-blue-500 focus:outline-none"
                />
              ) : (
                <h2 className="text-2xl font-bold text-gray-900">{task.title}</h2>
              )}
              <div className="mt-2 flex items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(task.status)}`}>
                  {task.status}
                </span>
                <span className="text-sm text-gray-500">
                  {getPriorityIcon(task.priority)} {task.priority}
                </span>
                {task.project_name && (
                  <span className="text-sm text-gray-500">
                    📁 {task.project_name}
                  </span>
                )}
              </div>
            </div>
            <div className="ml-4 flex items-center gap-2">
              <button
                onClick={handleToggleWatch}
                disabled={watchLoading}
                className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
                  isWatching
                    ? 'border-yellow-300 bg-yellow-50 text-yellow-800 hover:bg-yellow-100'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {watchLoading ? '...' : isWatching ? 'Unwatch' : 'Watch'}
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content - 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Description */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900">Description</h3>
                  {!editing && (
                    <button
                      onClick={() => setEditing(true)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editing ? (
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={6}
                    className="w-full rounded-lg border border-gray-300 p-3 focus:border-blue-500 focus:outline-none"
                    placeholder="Add a description..."
                  />
                ) : (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    {task.description || (
                      <span className="text-gray-400">No description provided</span>
                    )}
                  </div>
                )}
              </div>

              {/* Subtasks */}
              <div>
                <SubtasksPanel
                  parentTask={task}
                  organizationId={organizationId}
                  onUpdate={() => {
                    void fetchTaskDetails();
                    onUpdate();
                  }}
                />
              </div>

              {/* Comments Section */}
              <div>
                <CommentsPanel
                  taskId={taskId}
                  members={members}
                  onUpdate={() => {
                    void fetchTaskDetails();
                    onUpdate();
                  }}
                />
              </div>

              {/* Attachments Section */}
              <div>
                <AttachmentsPanel
                  taskId={taskId}
                  onUpdate={() => {
                    void fetchTaskDetails();
                    onUpdate();
                  }}
                />
              </div>
            </div>

            {/* Sidebar - 1 column */}
            <div className="space-y-4">
              {/* Details Card */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="mb-3 font-semibold text-gray-900">Details</h3>
                <div className="space-y-3 text-sm">
                  {editing ? (
                    <>
                      <div>
                        <label className="block text-gray-600 mb-1">Assignee</label>
                        <select
                          value={editAssigneeId}
                          onChange={(e) => setEditAssigneeId(e.target.value)}
                          className="w-full rounded border px-2 py-1"
                        >
                          <option value="">Unassigned</option>
                          {members.map(m => (
                            <option key={m.user_id} value={m.user_id}>
                              {getDisplayName(m)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Section</label>
                        <select
                          value={editSectionId}
                          onChange={(e) => setEditSectionId(e.target.value)}
                          className="w-full rounded border px-2 py-1"
                        >
                          <option value="">No Section</option>
                          {sections.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Status</label>
                        <select
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value)}
                          className="w-full rounded border px-2 py-1"
                        >
                          <option value="backlog">Backlog</option>
                          <option value="todo">To Do</option>
                          <option value="in-progress">In Progress</option>
                          <option value="review">Review</option>
                          <option value="done">Done</option>
                          <option value="blocked">Blocked</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Priority</label>
                        <select
                          value={editPriority}
                          onChange={(e) => setEditPriority(e.target.value)}
                          className="w-full rounded border px-2 py-1"
                        >
                          <option value="low">Low 🟢</option>
                          <option value="medium">Medium 🟡</option>
                          <option value="high">High 🟠</option>
                          <option value="urgent">Urgent 🔴</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={editDueDate}
                          onChange={(e) => setEditDueDate(e.target.value)}
                          className="w-full rounded border px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-600 mb-1">Estimated Hours</label>
                        <input
                          type="number"
                          value={editEstimatedTime}
                          onChange={(e) => setEditEstimatedTime(parseFloat(e.target.value) || 0)}
                          className="w-full rounded border px-2 py-1"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-gray-600">Assignee:</span>
                        <div className="mt-1 font-medium">
                          {task.assignee_id
                            ? getDisplayName(buildMemberMap(members)[task.assignee_id] || null)
                            : 'Unassigned'}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Section:</span>
                        <div className="mt-1 font-medium">
                          {task.section_name || 'No Section'}
                        </div>
                      </div>
                      {task.due_date && (
                        <div>
                          <span className="text-gray-600">Due Date:</span>
                          <div className="mt-1 font-medium">
                            {new Date(task.due_date).toLocaleDateString()}
                          </div>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Estimated Time:</span>
                        <div className="mt-1 font-medium">{task.estimated_time || 0}h</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Actual Time:</span>
                        <div className="mt-1 font-medium">{task.actual_time || 0}h</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Created:</span>
                        <div className="mt-1 font-medium">
                          {new Date(task.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {customFields.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-3 font-semibold text-gray-900">Custom Fields</h3>
                  <div className="space-y-3">
                    {customFields.map((field) => (
                      <CustomFieldInput
                        key={field.id}
                        field={field}
                        value={customFieldValues[field.field_key]}
                        disabled={!editing}
                        onChange={(value) => {
                          setCustomFieldValues((prev) => ({
                            ...prev,
                            [field.field_key]: value,
                          }));
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Labels */}
              {task.labels && task.labels.length > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-2 font-semibold text-gray-900">Labels</h3>
                  <div className="flex flex-wrap gap-2">
                    {task.labels.map((label: string, index: number) => (
                      <span
                        key={index}
                        className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity */}
              <ActivityFeedPanel
                organizationId={organizationId}
                entityType="task"
                entityId={taskId}
              />

              {/* Recurrence */}
              {editing && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-3 font-semibold text-gray-900">Recurrence</h3>
                  <RecurrenceSelector
                    value={recurrenceRule}
                    onChange={setRecurrenceRule}
                    endDate={recurrenceEndDate}
                    onEndDateChange={setRecurrenceEndDate}
                  />
                </div>
              )}
              {!editing && task.is_recurring && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <h3 className="mb-2 font-semibold text-gray-900">Recurrence</h3>
                  <div className="text-sm text-gray-700">
                    🔁 Recurring Task
                    {task.recurrence_end_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        Until {new Date(task.recurrence_end_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={handleDelete}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"
            >
              Delete Task
            </button>
            <div className="flex gap-3">
              {editing ? (
                <>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditTitle(task.title);
                      setEditDescription(task.description || '');
                      setCustomFieldValues(task.custom_fields || {});
                      setEditAssigneeId(task.assignee_id || '');
                      setEditSectionId(task.section_id || '');
                    }}
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Save Changes
                  </button>
                </>
              ) : (
                <button
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
